<template>
  <div class="color-picker-advanced" :class="{ 'is-bottom-sheet': isBottomSheet }">
    <div v-if="isBottomSheet" class="bottom-sheet-header">
      <span class="title">{{ title }}</span>
    </div>
    
    <div class="picker-body">
      <div 
        class="saturation-brightness-area" 
        ref="sbArea"
        :style="{ backgroundColor: pureHueColor }"
        @mousedown="startSBDrag"
        @touchstart.prevent="startSBDrag"
      >
        <div class="white-gradient"></div>
        <div class="black-gradient"></div>
        <div 
          class="sb-pointer" 
          :style="{ left: saturation + '%', top: (100 - brightness) + '%' }"
        ></div>
      </div>

      <div class="controls-row">
        <div class="current-color-preview" :style="{ backgroundColor: currentColor }"></div>
        
        <div 
          class="hue-slider" 
          ref="hueSlider"
          @mousedown="startHueDrag"
          @touchstart.prevent="startHueDrag"
        >
          <div class="hue-pointer" :style="{ left: (hue / 360) * 100 + '%' }"></div>
        </div>
      </div>

      <div class="rgb-inputs">
        <div class="rgb-input-group">
          <input 
            type="number" 
            v-model.number="red" 
            @input="updateFromRGB"
            min="0" 
            max="255"
          />
          <span class="label">R</span>
        </div>
        <div class="rgb-input-group">
          <input 
            type="number" 
            v-model.number="green" 
            @input="updateFromRGB"
            min="0" 
            max="255"
          />
          <span class="label">G</span>
        </div>
        <div class="rgb-input-group">
          <input 
            type="number" 
            v-model.number="blue" 
            @input="updateFromRGB"
            min="0" 
            max="255"
          />
          <span class="label">B</span>
        </div>
      </div>

      <div class="hex-input-row">
        <span class="hex-label">HEX:</span>
        <input 
          type="text" 
          v-model="hexInput" 
          @blur="updateFromHex"
          @keyup.enter="updateFromHex"
          class="hex-input"
          maxlength="7"
        />
      </div>

      <div class="preset-colors">
        <div 
          v-for="presetColor in presets" 
          :key="presetColor"
          class="preset-color"
          :style="{ backgroundColor: presetColor }"
          :class="{ active: currentColor.toUpperCase() === presetColor.toUpperCase() }"
          @click.stop="selectPreset(presetColor)"
          @touchend.stop.prevent="selectPreset(presetColor)"
        ></div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AppColorPicker',
  props: {
    modelValue: {
      type: String,
      default: '#000000'
    },
    isBottomSheet: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      default: 'Выбор цвета'
    },
    presets: {
      type: Array,
      default: () => [
        '#000000', '#FFFFFF', '#FF0000', '#00FF00', '#0000FF',
        '#FFFF00', '#FF00FF', '#00FFFF', '#FFA500', '#800080',
        '#008000', '#000080', '#808080', '#C0C0C0', '#A52A2A'
      ]
    }
  },
  emits: ['update:modelValue'],
  data() {
    return {
      hue: 0,
      saturation: 100,
      brightness: 100,
      red: 255,
      green: 0,
      blue: 0,
      hexInput: '#FF0000',
      isDraggingSB: false,
      isDraggingHue: false
    }
  },
  computed: {
    pureHueColor() {
      return `hsl(${this.hue}, 100%, 50%)`
    },
    currentColor() {
      return this.hsvToHex(this.hue, this.saturation, this.brightness)
    }
  },
  watch: {
    modelValue: {
      immediate: true,
      handler(newVal) {
        if (newVal && newVal !== this.currentColor) {
          this.setColorFromHex(newVal)
        }
      }
    },
    currentColor(newVal) {
      this.hexInput = newVal.toUpperCase()
      const rgb = this.hexToRgb(newVal)
      if (rgb) {
        this.red = rgb.r
        this.green = rgb.g
        this.blue = rgb.b
      }
      this.$emit('update:modelValue', newVal)
    }
  },
  methods: {
    setColorFromHex(hex) {
      const rgb = this.hexToRgb(hex)
      if (rgb) {
        this.red = rgb.r
        this.green = rgb.g
        this.blue = rgb.b
        const hsv = this.rgbToHsv(rgb.r, rgb.g, rgb.b)
        this.hue = hsv.h
        this.saturation = hsv.s
        this.brightness = hsv.v
        this.hexInput = hex.toUpperCase()
      }
    },

    startSBDrag(e) {
      this.isDraggingSB = true
      this.updateSB(e)
      document.addEventListener('mousemove', this.onSBMove)
      document.addEventListener('mouseup', this.stopSBDrag)
      document.addEventListener('touchmove', this.onSBMove)
      document.addEventListener('touchend', this.stopSBDrag)
    },

    onSBMove(e) {
      if (this.isDraggingSB) {
        this.updateSB(e)
      }
    },

    stopSBDrag() {
      this.isDraggingSB = false
      document.removeEventListener('mousemove', this.onSBMove)
      document.removeEventListener('mouseup', this.stopSBDrag)
      document.removeEventListener('touchmove', this.onSBMove)
      document.removeEventListener('touchend', this.stopSBDrag)
    },

    updateSB(e) {
      const rect = this.$refs.sbArea.getBoundingClientRect()
      const clientX = e.touches ? e.touches[0].clientX : e.clientX
      const clientY = e.touches ? e.touches[0].clientY : e.clientY
      
      let x = clientX - rect.left
      let y = clientY - rect.top
      
      x = Math.max(0, Math.min(x, rect.width))
      y = Math.max(0, Math.min(y, rect.height))
      
      this.saturation = (x / rect.width) * 100
      this.brightness = 100 - (y / rect.height) * 100
    },

    startHueDrag(e) {
      this.isDraggingHue = true
      this.updateHue(e)
      document.addEventListener('mousemove', this.onHueMove)
      document.addEventListener('mouseup', this.stopHueDrag)
      document.addEventListener('touchmove', this.onHueMove)
      document.addEventListener('touchend', this.stopHueDrag)
    },

    onHueMove(e) {
      if (this.isDraggingHue) {
        this.updateHue(e)
      }
    },

    stopHueDrag() {
      this.isDraggingHue = false
      document.removeEventListener('mousemove', this.onHueMove)
      document.removeEventListener('mouseup', this.stopHueDrag)
      document.removeEventListener('touchmove', this.onHueMove)
      document.removeEventListener('touchend', this.stopHueDrag)
    },

    updateHue(e) {
      const rect = this.$refs.hueSlider.getBoundingClientRect()
      const clientX = e.touches ? e.touches[0].clientX : e.clientX
      
      let x = clientX - rect.left
      x = Math.max(0, Math.min(x, rect.width))
      
      this.hue = (x / rect.width) * 360
    },

    updateFromRGB() {
      this.red = Math.max(0, Math.min(255, this.red || 0))
      this.green = Math.max(0, Math.min(255, this.green || 0))
      this.blue = Math.max(0, Math.min(255, this.blue || 0))
      
      const hsv = this.rgbToHsv(this.red, this.green, this.blue)
      this.hue = hsv.h
      this.saturation = hsv.s
      this.brightness = hsv.v
    },

    updateFromHex() {
      let hex = this.hexInput.trim()
      if (!hex.startsWith('#')) {
        hex = '#' + hex
      }
      if (/^#[0-9A-Fa-f]{6}$/.test(hex)) {
        this.setColorFromHex(hex)
      }
    },

    selectPreset(color) {
      this.setColorFromHex(color)
    },

    hsvToHex(h, s, v) {
      s = s / 100
      v = v / 100
      
      let r, g, b
      const i = Math.floor(h / 60)
      const f = h / 60 - i
      const p = v * (1 - s)
      const q = v * (1 - f * s)
      const t = v * (1 - (1 - f) * s)
      
      switch (i % 6) {
        case 0: r = v; g = t; b = p; break
        case 1: r = q; g = v; b = p; break
        case 2: r = p; g = v; b = t; break
        case 3: r = p; g = q; b = v; break
        case 4: r = t; g = p; b = v; break
        case 5: r = v; g = p; b = q; break
      }
      
      const toHex = (x) => {
        const hex = Math.round(x * 255).toString(16)
        return hex.length === 1 ? '0' + hex : hex
      }
      
      return `#${toHex(r)}${toHex(g)}${toHex(b)}`.toUpperCase()
    },

    hexToRgb(hex) {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
      return result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
      } : null
    },

    rgbToHsv(r, g, b) {
      r /= 255
      g /= 255
      b /= 255
      
      const max = Math.max(r, g, b)
      const min = Math.min(r, g, b)
      const d = max - min
      
      let h = 0
      const s = max === 0 ? 0 : (d / max) * 100
      const v = max * 100
      
      if (d !== 0) {
        switch (max) {
          case r: h = ((g - b) / d + (g < b ? 6 : 0)) * 60; break
          case g: h = ((b - r) / d + 2) * 60; break
          case b: h = ((r - g) / d + 4) * 60; break
        }
      }
      
      return { h, s, v }
    }
  },
  beforeUnmount() {
    document.removeEventListener('mousemove', this.onSBMove)
    document.removeEventListener('mouseup', this.stopSBDrag)
    document.removeEventListener('touchmove', this.onSBMove)
    document.removeEventListener('touchend', this.stopSBDrag)
    document.removeEventListener('mousemove', this.onHueMove)
    document.removeEventListener('mouseup', this.stopHueDrag)
    document.removeEventListener('touchmove', this.onHueMove)
    document.removeEventListener('touchend', this.stopHueDrag)
  }
}
</script>

<style scoped>
.color-picker-advanced {
  background: #fff;
  border-radius: 8px;
  padding: 10px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  width: 200px;
  user-select: none;
}

.color-picker-advanced.is-bottom-sheet {
  width: 100%;
  max-width: 280px;
  margin: 0 auto;
  border-radius: 12px 12px 0 0;
  box-shadow: none;
}

.bottom-sheet-header {
  text-align: center;
  margin-bottom: 10px;
  padding-bottom: 8px;
  border-bottom: 1px solid #eee;
}

.bottom-sheet-header .title {
  font-size: 14px;
  font-weight: 600;
  color: #333;
}

.picker-body {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.saturation-brightness-area {
  position: relative;
  width: 100%;
  height: 120px;
  border-radius: 6px;
  cursor: crosshair;
  overflow: hidden;
  touch-action: none;
}

.white-gradient {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(to right, #fff, transparent);
}

.black-gradient {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(to bottom, transparent, #000);
}

.sb-pointer {
  position: absolute;
  width: 12px;
  height: 12px;
  border: 2px solid #fff;
  border-radius: 50%;
  box-shadow: 0 0 3px rgba(0, 0, 0, 0.5);
  transform: translate(-50%, -50%);
  pointer-events: none;
}

.controls-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.current-color-preview {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 2px solid #ddd;
  flex-shrink: 0;
}

.hue-slider {
  flex: 1;
  height: 12px;
  border-radius: 6px;
  background: linear-gradient(to right, 
    #ff0000 0%, 
    #ffff00 17%, 
    #00ff00 33%, 
    #00ffff 50%, 
    #0000ff 67%, 
    #ff00ff 83%, 
    #ff0000 100%
  );
  position: relative;
  cursor: pointer;
  touch-action: none;
}

.hue-pointer {
  position: absolute;
  top: 50%;
  width: 8px;
  height: 16px;
  background: #fff;
  border: 2px solid #333;
  border-radius: 3px;
  transform: translate(-50%, -50%);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  pointer-events: none;
}

.rgb-inputs {
  display: flex;
  gap: 4px;
  justify-content: center;
}

.rgb-input-group {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
}

.rgb-input-group input {
  width: 44px;
  height: 26px;
  text-align: center;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
}

.rgb-input-group input:focus {
  outline: none;
  border-color: #26374E;
}

.rgb-input-group .label {
  font-size: 10px;
  color: #666;
  font-weight: 500;
}

.hex-input-row {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.hex-label {
  font-size: 12px;
  color: #666;
  font-weight: 500;
}

.hex-input {
  width: 70px;
  height: 26px;
  text-align: center;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  text-transform: uppercase;
}

.hex-input:focus {
  outline: none;
  border-color: #26374E;
}

.preset-colors {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  justify-content: center;
  padding-top: 6px;
  border-top: 1px solid #eee;
}

.preset-color {
  width: 20px;
  height: 20px;
  border-radius: 4px;
  border: 1px solid #ddd;
  cursor: pointer;
  transition: transform 0.2s, border-color 0.2s;
}

.preset-color:hover {
  transform: scale(1.1);
}

.preset-color.active {
  border-color: #26374E;
  border-width: 2px;
}

@media (max-width: 768px) {
  .color-picker-advanced {
    width: 100%;
    padding: 10px;
  }

  .color-picker-advanced.is-bottom-sheet {
    max-width: 100%;
    padding: 12px;
  }

  .saturation-brightness-area {
    height: 100px;
  }

  .rgb-input-group input {
    width: 50px;
    height: 28px;
    font-size: 12px;
  }

  .preset-color {
    width: 24px;
    height: 24px;
  }
}
</style>
